import React, { useState, useEffect, useRef } from 'react';
import {
  Send,
  X,
  Settings,
  Trash2,
  Key,
  Eye,
  EyeOff,
  Lightbulb,
  Check,
  AlertCircle,
  Sparkles,
  Bot,
  Database,
} from 'lucide-react';
import SpeakButton from '../ui/SpeakButton';
import { useAuth } from '../../contexts/AuthContext';
import {
  sendTutorMessage,
  getSavedApiKey,
  saveApiKey,
  getSavedModel,
  saveModel,
  fetchApiKeyFromSupabase,
  getStoredChatHistory,
  saveStoredChatHistory,
  clearStoredChatHistory,
  DEFAULT_MODEL,
} from '../../services/aiTutorService';
import './AITutorWidget.css';

const INITIAL_B2_WELCOME = {
  id: 'welcome_msg_b2',
  role: 'tutor',
  content: `Përshëndetje! Unë jam Maximilian, mentori yt për gjermanishten B2. 🇩🇪\n\nMund të më shkruash në gjermanisht për të ushtruar bisedën, për pyetje gramatikore, ose për t'u përgatitur për provimet Goethe dhe telc.`,
  correction: null,
  newWords: [
    { de: 'in Betracht ziehen', sq: 'marr parasysh' },
    { de: 'einen Beitrag leisten', sq: 'jap kontribut' },
  ],
  suggestedReplies: [
    'Si të përgatitem për provimin B2?',
    'Më shpjego Passiv me shembuj praktikë.',
    'Më jep një ushtrim me fjali të përbëra.',
  ],
  timestamp: new Date().toISOString(),
};

export default function AITutorFloatingWidget() {
  const { isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // Settings State
  const [apiKey, setApiKey] = useState(() => getSavedApiKey());
  const [tempApiKey, setTempApiKey] = useState(() => getSavedApiKey());
  const [showApiKeyText, setShowApiKeyText] = useState(false);
  const [model, setModel] = useState(() => getSavedModel() || DEFAULT_MODEL);
  const [keySavedBanner, setKeySavedBanner] = useState(false);
  const [isSupabaseKeyActive, setIsSupabaseKeyActive] = useState(false);

  // Chat State
  const [messages, setMessages] = useState(() => {
    const history = getStoredChatHistory();
    return history && history.length > 0 ? history : [INITIAL_B2_WELCOME];
  });
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const widgetRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check Supabase for configured OpenAI key on mount
  useEffect(() => {
    let isMounted = true;
    fetchApiKeyFromSupabase().then((supaKey) => {
      if (isMounted && supaKey) {
        setIsSupabaseKeyActive(true);
        if (!apiKey) {
          setApiKey(supaKey);
          setTempApiKey(supaKey);
        }
      }
    });
    return () => {
      isMounted = false;
    };
  }, [apiKey]);

  // Auto-scroll on new messages or when opened
  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen, loading]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [isOpen]);

  // Save history to localStorage
  useEffect(() => {
    saveStoredChatHistory(messages);
  }, [messages]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        isOpen &&
        widgetRef.current &&
        !widgetRef.current.contains(event.target) &&
        !showSettings
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, showSettings]);

  const handleSaveSettings = (e) => {
    e.preventDefault();
    saveApiKey(tempApiKey);
    saveModel(model);
    setApiKey(tempApiKey);
    setShowSettings(false);
    setKeySavedBanner(true);
    setTimeout(() => setKeySavedBanner(false), 3000);
  };

  const handleClearHistory = () => {
    clearStoredChatHistory();
    setMessages([INITIAL_B2_WELCOME]);
    setShowClearConfirm(false);
  };

  const handleSendMessage = async (textToSend = null) => {
    const text = (textToSend || input).trim();
    if (!text || loading) return;

    setInput('');
    setError(null);

    const userMsg = {
      id: `user_${Date.now()}`,
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);

    try {
      const response = await sendTutorMessage({
        messages: nextMessages,
        apiKey,
        model,
      });

      const tutorMsg = {
        id: `tutor_${Date.now()}`,
        role: 'tutor',
        content: response.reply,
        correction: response.correction,
        newWords: response.newWords || [],
        suggestedReplies: response.suggestedReplies || [],
        isDemo: Boolean(response.isDemo),
        timestamp: new Date().toISOString(),
      };

      setMessages((prev) => [...prev, tutorMsg]);

      if (!isOpen) {
        setUnreadCount((prev) => prev + 1);
      }
    } catch (err) {
      console.error('AI Tutor error:', err);
      setError(err.message || 'Ndodhi një gabim gjatë lidhjes me AI.');
    } finally {
      setLoading(false);
    }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="ai-tutor-container" ref={widgetRef}>
      {/* ─── 1. CHAT WINDOW ─── */}
      {isOpen && (
        <div className="ai-tutor-window">
          {/* Header */}
          <div className="ai-tutor-header">
            <div className="ai-tutor-header-info">
              <div className="ai-tutor-avatar">
                <Bot size={20} />
                <span className="ai-tutor-status-dot" />
              </div>
              <div className="ai-tutor-header-text">
                <h3>
                  Maximilian
                  <span className="ai-tutor-level-badge">B2 Mentor</span>
                </h3>
                <p>Ndihmësi yt i gjermanishtes</p>
              </div>
            </div>

            <div className="ai-tutor-header-actions">
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="ai-tutor-icon-btn"
                title="Cilësimet"
                aria-label="Cilësimet"
              >
                <Settings size={16} />
              </button>

              <button
                type="button"
                onClick={() => setShowClearConfirm(true)}
                className="ai-tutor-icon-btn danger"
                title="Pastro bisedën"
                aria-label="Pastro bisedën"
              >
                <Trash2 size={16} />
              </button>

              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="ai-tutor-icon-btn"
                title="Mbyll"
                aria-label="Mbyll"
              >
                <X size={17} />
              </button>
            </div>
          </div>

          {/* Key Saved Banner */}
          {keySavedBanner && (
            <div className="flex items-center gap-2 bg-emerald-500 text-white px-3.5 py-1.5 text-xs font-medium">
              <Check size={14} />
              <span>Cilësimet u ruajtën me sukses!</span>
            </div>
          )}

          {/* Demo Mode Notice */}
          {!apiKey && !isSupabaseKeyActive && (
            <div className="flex items-center justify-between gap-2 bg-amber-50 dark:bg-amber-950/30 border-b border-amber-200/60 dark:border-amber-900/40 px-3.5 py-1.5 text-xs text-amber-800 dark:text-amber-300">
              <div className="flex items-center gap-1.5 truncate">
                <Sparkles size={13} className="text-amber-500 shrink-0" />
                <span className="truncate">Modalitet Demo (përgjigje të simuluara)</span>
              </div>
              <button
                type="button"
                onClick={() => setShowSettings(true)}
                className="shrink-0 font-semibold underline hover:text-amber-950 dark:hover:text-amber-100 cursor-pointer text-[11px]"
              >
                Shto çelës
              </button>
            </div>
          )}

          {/* Supabase Active Key Notice */}
          {isSupabaseKeyActive && !apiKey && (
            <div className="flex items-center gap-1.5 bg-zinc-50 dark:bg-zinc-800/50 border-b border-zinc-200 dark:border-zinc-800 px-3.5 py-1 text-[11px] text-zinc-600 dark:text-zinc-400">
              <Database size={12} className="shrink-0 text-emerald-500" />
              <span>Lidhur me çelësin OpenAI nga Supabase</span>
            </div>
          )}

          {/* Clear History Confirmation Bar */}
          {showClearConfirm && (
            <div className="flex items-center justify-between gap-2 bg-zinc-900 text-white px-3.5 py-2 text-xs border-b border-zinc-800">
              <span className="text-zinc-300">Ta fshijmë historikun e bisedës?</span>
              <div className="flex items-center gap-1.5 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowClearConfirm(false)}
                  className="px-2 py-0.5 rounded text-[11px] font-medium text-zinc-400 hover:text-white"
                >
                  Anulo
                </button>
                <button
                  type="button"
                  onClick={handleClearHistory}
                  className="px-2.5 py-0.5 rounded text-[11px] font-semibold bg-red-600 text-white hover:bg-red-700"
                >
                  Fshij
                </button>
              </div>
            </div>
          )}

          {/* Messages Stream */}
          <div className="ai-tutor-messages">
            {messages.map((msg) => {
              const isUser = msg.role === 'user';

              return (
                <div
                  key={msg.id}
                  className={`ai-tutor-msg-wrap ${isUser ? 'user' : 'assistant'}`}
                >
                  <div className={`ai-tutor-bubble ${isUser ? 'user' : 'assistant'}`}>
                    <div>{msg.content}</div>

                    {!isUser && msg.content && (
                      <div className="ai-tutor-speak-bar">
                        <SpeakButton text={msg.content} lang="de-DE" />
                      </div>
                    )}
                  </div>

                  {/* Grammar Correction Callout */}
                  {!isUser && msg.correction && (
                    <div className="ai-tutor-correction-box">
                      <div className="ai-tutor-correction-title">
                        <Lightbulb size={13} />
                        <span>Këshillë gramatikore</span>
                      </div>
                      <p>{msg.correction}</p>
                    </div>
                  )}

                  {/* Vocabulary Chips */}
                  {!isUser && msg.newWords && msg.newWords.length > 0 && (
                    <div className="ai-tutor-vocab-chips">
                      {msg.newWords.map((w, idx) => (
                        <div key={idx} className="ai-tutor-vocab-chip">
                          <strong>{w.de}</strong>
                          {w.sq && <span>({w.sq})</span>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Suggested Replies */}
                  {!isUser && msg.suggestedReplies && msg.suggestedReplies.length > 0 && (
                    <div className="ai-tutor-suggestions">
                      {msg.suggestedReplies.map((reply, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSendMessage(reply)}
                          className="ai-tutor-suggestion-chip"
                        >
                          {reply}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}

            {/* Typing Indicator */}
            {loading && (
              <div className="ai-tutor-msg-wrap assistant">
                <div className="ai-tutor-typing">
                  <div className="ai-tutor-dot" />
                  <div className="ai-tutor-dot" />
                  <div className="ai-tutor-dot" />
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 dark:bg-red-950/20 dark:border-red-900/50 p-2.5 text-xs text-red-700 dark:text-red-300">
                <AlertCircle size={15} className="text-red-500 shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium">{error}</p>
                  <button
                    type="button"
                    onClick={() => setShowSettings(true)}
                    className="mt-1 text-[11px] font-semibold underline cursor-pointer"
                  >
                    Kontrollo cilësimet
                  </button>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendMessage();
            }}
            className="ai-tutor-input-area"
          >
            <div className="ai-tutor-input-wrapper">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Shkruaj në gjermanisht..."
                disabled={loading}
                className="ai-tutor-input"
              />
            </div>
            <button
              type="submit"
              disabled={!input.trim() || loading}
              className="ai-tutor-send-btn"
              title="Dërgo mesazhin"
              aria-label="Dërgo mesazhin"
            >
              <Send size={15} />
            </button>
          </form>

          {/* ─── SETTINGS MODAL OVERLAY ─── */}
          {showSettings && (
            <div className="absolute inset-0 z-50 flex flex-col bg-white dark:bg-zinc-900 p-5 text-zinc-900 dark:text-white">
              <div className="flex items-center justify-between border-b border-zinc-100 dark:border-zinc-800 pb-3">
                <div className="flex items-center gap-2.5">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300">
                    <Key size={15} />
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold">Cilësimet e AI</h4>
                    <p className="text-[11px] text-zinc-500 dark:text-zinc-400">Konfiguro çelësin dhe modelin</p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowSettings(false)}
                  className="rounded-lg p-1 text-zinc-400 hover:text-zinc-600 dark:hover:text-white transition-colors"
                >
                  <X size={17} />
                </button>
              </div>

              <form onSubmit={handleSaveSettings} className="space-y-4 pt-4 flex-1 flex flex-col justify-between">
                <div className="space-y-3.5">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1">
                      OpenAI API Key
                    </label>
                    <div className="relative">
                      <input
                        type={showApiKeyText ? 'text' : 'password'}
                        value={tempApiKey}
                        onChange={(e) => setTempApiKey(e.target.value)}
                        placeholder="sk-proj-... ose nga Supabase"
                        className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 pl-3 pr-9 py-2 text-xs font-mono text-zinc-900 dark:text-white focus:border-zinc-400 focus:bg-white dark:focus:bg-zinc-900 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setShowApiKeyText(!showApiKeyText)}
                        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                        title={showApiKeyText ? 'Fshih çelësin' : 'Shfaq çelësin'}
                      >
                        {showApiKeyText ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                    </div>
                    <p className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
                      Mund ta ruash këtu lokalisht, ose në Supabase te tabela <code className="font-mono text-zinc-700 dark:text-zinc-300">app_settings</code>.
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 dark:text-zinc-300 mb-1">
                      Modeli OpenAI
                    </label>
                    <select
                      value={model}
                      onChange={(e) => setModel(e.target.value)}
                      className="w-full rounded-xl border border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800 px-3 py-2 text-xs text-zinc-900 dark:text-white focus:border-zinc-400 focus:outline-none cursor-pointer"
                    >
                      <option value="gpt-4o-mini">gpt-4o-mini (I shpejtë, ekonomik, i rekomanduar)</option>
                      <option value="gpt-4o">gpt-4o (Më i saktë për nivele të larta)</option>
                      <option value="gpt-3.5-turbo">gpt-3.5-turbo (Standard)</option>
                    </select>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-2 pt-4 border-t border-zinc-100 dark:border-zinc-800">
                  <button
                    type="button"
                    onClick={() => {
                      setTempApiKey('');
                      saveApiKey('');
                      setApiKey('');
                      setShowSettings(false);
                    }}
                    className="text-xs font-medium text-zinc-500 hover:text-red-500 transition-colors cursor-pointer"
                  >
                    Pastro çelësin
                  </button>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setShowSettings(false)}
                      className="px-3 py-1.5 rounded-lg text-xs font-medium text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors cursor-pointer"
                    >
                      Anulo
                    </button>
                    <button
                      type="submit"
                      className="rounded-lg bg-zinc-900 dark:bg-white hover:bg-zinc-800 dark:hover:bg-zinc-100 text-white dark:text-zinc-900 px-4 py-1.5 text-xs font-semibold shadow-sm transition-colors cursor-pointer"
                    >
                      Ruaj
                    </button>
                  </div>
                </div>
              </form>
            </div>
          )}
        </div>
      )}

      {/* ─── 2. FLOATING ACTION BUTTON (FAB) ─── */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => {
            setIsOpen(true);
            setUnreadCount(0);
          }}
          className="ai-tutor-fab"
          title="Hap bisedën me Maximilian AI"
          aria-label="Hap bisedën me Maximilian AI"
        >
          <Bot size={24} />
          {unreadCount > 0 && (
            <span className="ai-tutor-badge">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
